import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assignableRoles } from '@/application/assistants/assignableRoles';
import { looksLikePersonName, normalizePersonName } from '@/application/assistants/personName';
import type { VerifiedAssistant } from '@/application/assistants/assistantsTypes';
import type { Role } from '@/application/permissions/permissionsTypes';
import { assignAssistantRole, createAssistant, verifyAssistantEmail } from '@/infrastructure/http/assistantsApi';
import { createRole } from '@/infrastructure/http/permissionsApi';
import {
  MAX_CUSTOM_ROLES,
  findRoleMatchingTemplate,
  nextUniqueRoleName,
  templateById,
  type RoleTemplateId,
} from '@/shared/permissions/roleTemplates';
import { RoleTemplatePicker } from '@/presentation/components/permissions/RoleTemplatePicker';
import { ClinicsPicker } from '@/presentation/components/assistants/ClinicsPicker';
import { ModalPortal } from '@/presentation/components/common/ModalPortal';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';
import { useVisualViewportBottomInset } from '@/presentation/hooks/useVisualViewportBottomInset';

const STEPS = ['email', 'clinics', 'role', 'password'] as const;
const LAST_INDEX = STEPS.length - 1;

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function CreateAssistantWizard({
  token,
  ownerClinics,
  roles,
  nameFor,
  onClose,
  onCreated,
}: {
  token: string;
  ownerClinics: string[];
  roles: Role[];
  nameFor: (clinicId: string) => string;
  onClose: () => void;
  onCreated: (info?: { roleAssignFailed?: boolean }) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formClinics, setFormClinics] = useState<string[]>([]);
  const [roleId, setRoleId] = useState('');
  const [templateId, setTemplateId] = useState<RoleTemplateId | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState<VerifiedAssistant | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const backdropPressRef = useRef(false);
  const keyboardInset = useVisualViewportBottomInset();

  const errorText = useCallback(
    (code: string) => t(`assistants.errors.${code}`, { defaultValue: t('assistants.errors.generic') }),
    [t]
  );

  const isVerified = verified !== null && verifiedEmail === email.trim();
  const step = STEPS[stepIndex];
  const dirty = email.trim().length > 0 || isVerified || password.length > 0 || formClinics.length > 0;
  const selectedTemplate = templateId ? templateById(templateId) : undefined;

  const requestClose = useCallback(() => {
    if (submitting) return;
    if (dirty && !confirmingClose) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }, [confirmingClose, dirty, onClose, submitting]);

  useKeyEsc(requestClose);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setStepError(null);
    if (value.trim() !== verifiedEmail) {
      setVerified(null);
      setVerifiedEmail(null);
      setVerifyError(null);
      setNombre('');
    }
  };

  const commitEmail = (value: string) => {
    const trimmed = value.trim();
    if (trimmed !== value) setEmail(trimmed);
    return trimmed;
  };

  const handleVerify = async () => {
    const trimmed = email.trim();
    setVerifyError(null);
    setStepError(null);
    if (!looksLikeEmail(trimmed)) {
      setVerifyError(errorText('panel_usuario_email_invalid'));
      return;
    }
    setVerifying(true);
    try {
      const result = await verifyAssistantEmail(token, trimmed);
      setVerified(result);
      setEmail(trimmed);
      setVerifiedEmail(trimmed);
      const name = [result.nombre, result.apellido].filter(Boolean).join(' ').trim();
      if (name) {
        setNombre(name);
        if (!looksLikePersonName(name)) {
          setStepError(t('assistants.wizard.nameInvalid'));
        }
      }
      if (result.clinicaIds.length > 0) {
        setFormClinics(result.clinicaIds);
      }
    } catch (err) {
      setVerified(null);
      setVerifiedEmail(null);
      setNombre('');
      setVerifyError(errorText(err instanceof Error ? err.message : 'generic'));
    } finally {
      setVerifying(false);
    }
  };

  const goNext = () => {
    setStepError(null);
    if (step === 'email') {
      commitEmail(email);
      if (!isVerified) {
        setStepError(t('assistants.verify.required'));
        return;
      }
      const name = normalizePersonName(nombre);
      setNombre(name);
      if (!name) {
        setStepError(errorText('panel_usuario_nombre_required'));
        return;
      }
      if (!looksLikePersonName(name)) {
        setStepError(t('assistants.wizard.nameInvalid'));
        return;
      }
    }
    if (step === 'clinics' && formClinics.length === 0) {
      setStepError(t('assistants.wizard.clinicsRequired'));
      return;
    }
    setStepIndex((current) => Math.min(current + 1, LAST_INDEX));
  };

  const goBack = () => {
    setStepError(null);
    setConfirmingClose(false);
    if (step === 'password') setShowPassword(false);
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const resolveWizardRoleId = async (): Promise<string | null> => {
    if (roleId) return roleId;
    if (!templateId) return null;
    const template = templateById(templateId);
    if (!template) return null;
    const existing = findRoleMatchingTemplate(assignableRoles(roles), template);
    if (existing) return existing.id;
    if (roles.filter((role) => !role.isSystem).length >= MAX_CUSTOM_ROLES) {
      throw new Error('panel_role_limit');
    }
    const createdRole = await createRole(token, {
      name: nextUniqueRoleName(
        t(template.savedNameKey),
        roles.map((role) => role.name)
      ),
      permissionKeys: [...template.permissionKeys],
      isActive: true,
    });
    return createdRole.id;
  };

  const handleCreate = async () => {
    if (!isVerified) return;
    setStepError(null);
    if (!looksLikePersonName(nombre)) {
      setStepError(t('assistants.wizard.nameInvalid'));
      return;
    }
    if (password.length < 8) {
      setStepError(errorText('assistant_password_invalid'));
      return;
    }
    if (password !== passwordConfirm) {
      setStepError(t('assistants.wizard.passwordMismatch'));
      return;
    }
    if (formClinics.length === 0) {
      setStepError(t('assistants.wizard.clinicsRequired'));
      return;
    }

    setSubmitting(true);
    const name = normalizePersonName(nombre);
    setNombre(name);
    try {
      const assignedRoleId = await resolveWizardRoleId();
      const created = await createAssistant(token, {
        email: email.trim(),
        nombre: name,
        password,
        clinicaIds: formClinics,
      });
      let roleAssignFailed = false;
      if (assignedRoleId) {
        try {
          await assignAssistantRole(token, created.id, assignedRoleId);
        } catch {
          roleAssignFailed = true;
        }
      }
      await onCreated(roleAssignFailed ? { roleAssignFailed: true } : undefined);
      onClose();
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generic';
      if (code === 'panel_role_limit') {
        setStepError(t('permissions.limitReached', { max: MAX_CUSTOM_ROLES }));
      } else {
        setStepError(
          t(`permissions.errors.${code}`, {
            defaultValue: t(`assistants.errors.${code}`, { defaultValue: t('assistants.errors.generic') }),
          })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitle = t(`assistants.wizard.steps.${step}`);

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      role="presentation"
      data-testid="assistants-wizard-backdrop"
      onPointerDown={(event) => {
        event.stopPropagation();
        backdropPressRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        event.stopPropagation();
        const startedOnBackdrop = backdropPressRef.current;
        backdropPressRef.current = false;
        if (startedOnBackdrop && event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-assistant-title"
        data-testid="assistants-create-wizard"
        className="flex min-h-0 max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4 dark:border-gray-700">
          <h2 id="create-assistant-title" className="font-bold text-gray-900 dark:text-white">
            {t('assistants.wizard.title')}
          </h2>
          <p data-testid="assistants-wizard-progress" className="mt-0.5 text-xs font-medium text-teal-700 dark:text-teal-300">
            {t('assistants.wizard.progress', { current: stepIndex + 1, total: STEPS.length })}
            {' · '}
            {stepTitle}
          </p>
        </div>

        <ol className="flex gap-1 px-4 pt-3 sm:px-5" aria-hidden="true">
          {STEPS.map((id, index) => (
            <li
              key={id}
              className={`h-1 flex-1 rounded-full ${index <= stepIndex ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-700'}`}
            />
          ))}
        </ol>

        <div className="space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          {confirmingClose ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('assistants.wizard.discardTitle')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('assistants.wizard.discardMessage')}</p>
            </div>
          ) : (
            <>
              {stepError && (
                <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
                  {stepError}
                </p>
              )}

              {step === 'email' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('assistants.wizard.emailHint')}</p>
                  <div>
                    <label htmlFor="assistant-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('assistants.new.email')}
                    </label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        id="assistant-email"
                        type="email"
                        value={email}
                        onChange={(event) => handleEmailChange(event.target.value)}
                        onBlur={() => commitEmail(email)}
                        autoComplete="off"
                        autoFocus
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                      />
                      <button
                        type="button"
                        onClick={handleVerify}
                        disabled={verifying || !email.trim() || isVerified}
                        className="whitespace-nowrap rounded-md border border-teal-600 px-3 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-50 disabled:opacity-50 dark:text-teal-400 dark:hover:bg-teal-900/20"
                      >
                        {verifying
                          ? t('assistants.new.verifying')
                          : isVerified
                            ? t('assistants.verify.verified')
                            : t('assistants.new.verify')}
                      </button>
                    </div>
                  </div>

                  {verifyError && (
                    <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
                      {verifyError}
                    </p>
                  )}

                  {isVerified && verified && (
                    <>
                      <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm dark:border-teal-800 dark:bg-teal-900/20">
                        <p className="font-semibold text-teal-800 dark:text-teal-200">{t('assistants.verify.matchTitle')}</p>
                        <ul className="mt-1 space-y-0.5 text-teal-700 dark:text-teal-300">
                          <li>
                            {t('assistants.verify.status')}:{' '}
                            {verified.activo ? t('assistants.verify.active') : t('assistants.verify.inactive')}
                          </li>
                          {verified.telefono && (
                            <li>
                              {t('assistants.verify.phone')}: {verified.telefono}
                            </li>
                          )}
                          <li>{t('assistants.verify.clinicsPreselected', { count: verified.clinicaIds.length })}</li>
                          {verified.isOwner && (
                            <li className="font-medium text-amber-700 dark:text-amber-400">{t('assistants.verify.isOwnerWarning')}</li>
                          )}
                        </ul>
                      </div>

                      <div>
                        <label htmlFor="assistant-nombre" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('assistants.new.nombre')}
                        </label>
                        <input
                          id="assistant-nombre"
                          value={nombre}
                          onChange={(event) => {
                            setNombre(event.target.value);
                            setStepError(null);
                          }}
                          onBlur={() => setNombre(normalizePersonName(nombre))}
                          aria-invalid={Boolean(stepError)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('assistants.wizard.nameHint')}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 'clinics' && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('assistants.new.clinics')}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('assistants.wizard.clinicsHint')}</p>
                  <ClinicsPicker
                    clinics={ownerClinics}
                    selected={formClinics}
                    onChange={setFormClinics}
                    idPrefix="wizard-clinics"
                    nameFor={nameFor}
                  />
                </div>
              )}

              {step === 'role' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('assistants.wizard.roleHint')}</p>
                  <RoleTemplatePicker
                    selectedId={templateId}
                    showCustom={false}
                    hideHint
                    onSelect={(id) => {
                      if (id === 'custom') return;
                      setTemplateId(id);
                      setRoleId('');
                      setStepError(null);
                    }}
                  />
                  <div>
                    <label htmlFor="assistant-role" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('assistants.wizard.roleExisting')}
                    </label>
                    <select
                      id="assistant-role"
                      aria-label={t('assistants.role.label')}
                      value={roleId || (templateId ? `template:${templateId}` : '')}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next.startsWith('template:')) {
                          setRoleId('');
                          setTemplateId(next.slice('template:'.length) as RoleTemplateId);
                        } else {
                          setRoleId(next);
                          setTemplateId(null);
                        }
                        setStepError(null);
                      }}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                    >
                      <option value="">{t('assistants.wizard.roleDefault')}</option>
                      {selectedTemplate && (
                        <option value={`template:${selectedTemplate.id}`}>
                          {t('assistants.wizard.roleFromTemplate', { name: t(selectedTemplate.nameKey) })}
                        </option>
                      )}
                      {assignableRoles(roles).map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {step === 'password' && (
                <div className="space-y-3">
                  <div>
                    <label htmlFor="assistant-password" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('assistants.new.password')}
                    </label>
                    <div className="relative">
                      <input
                        id="assistant-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        minLength={8}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 pr-11 text-sm dark:border-gray-700 dark:bg-gray-900"
                      />
                      <PasswordRevealButton
                        visible={showPassword}
                        onToggle={() => setShowPassword((value) => !value)}
                        showLabel={t('auth.showPassword')}
                        hideLabel={t('auth.hidePassword')}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('assistants.password.hint')}</p>
                  </div>
                  <div>
                    <label
                      htmlFor="assistant-password-confirm"
                      className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('assistants.wizard.passwordConfirm')}
                    </label>
                    <input
                      id="assistant-password-confirm"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={passwordConfirm}
                      onChange={(event) => setPasswordConfirm(event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:justify-end sm:px-5 sm:py-4 dark:border-gray-700">
          {confirmingClose ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmingClose(false)}
                className="w-full rounded-lg px-4 py-2 text-sm text-gray-600 sm:w-auto dark:text-gray-300"
              >
                {t('assistants.wizard.discardKeep')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto"
              >
                {t('assistants.wizard.discardConfirm')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={requestClose}
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto"
              >
                {t('assistants.actions.cancel')}
              </button>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 sm:w-auto dark:border-gray-600 dark:text-gray-200"
                >
                  {t('common.back')}
                </button>
              )}
              {step !== 'password' ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={step === 'email' && !isVerified}
                  className="w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-teal-600 sm:w-auto"
                >
                  {t('assistants.wizard.next')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={submitting || !isVerified}
                  className="w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-teal-600 sm:w-auto"
                >
                  {submitting ? t('assistants.new.submitting') : t('assistants.new.submit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

function PasswordRevealButton({
  visible,
  onToggle,
  showLabel,
  hideLabel,
}: {
  visible: boolean;
  onToggle: () => void;
  showLabel: string;
  hideLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
      aria-label={visible ? hideLabel : showLabel}
      aria-pressed={visible}
      tabIndex={-1}
    >
      {visible ? (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42L21 21M12 12l.01.01"
          />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      )}
    </button>
  );
}
